import type { ReactNode } from "react";
import { StatusDot } from "@/components/connectors/status-dot";
import { formatShortTimeAgo } from "@/lib/format-utils";
import { cn } from "@/lib/utils";

/**
 * Row chrome every source list shares (Home, Automations, Connectors, Agents):
 * the clickable row a page wraps around {@link ListRowBody}.
 */
export const LIST_ROW_LAYOUT =
	"flex w-full items-center gap-3 px-3 py-2.5 text-left";
/** The row hover. A row with its own buttons puts it on the whole row. */
export const LIST_ROW_HOVER = "transition-colors hover:bg-accent/50";
export const LIST_ROW_CLASS = `${LIST_ROW_LAYOUT} ${LIST_ROW_HOVER}`;

/**
 * One source as a row: its mark in a tile, the name, one quiet line and the
 * time it last did something. Colour means a problem: a dot before the name,
 * never on a healthy row; the line itself stays muted.
 */
export function ListRowBody({
	mark,
	name,
	badge,
	tone,
	details,
	at,
	atClassName,
}: {
	/** Omit for a row nested under a parent that already shows the mark. */
	mark?: ReactNode;
	name: ReactNode;
	/** Next to the name, e.g. "Archived". */
	badge?: ReactNode;
	/** Set only when the row has a problem or needs you. */
	tone?: "destructive" | "amber" | null;
	/** The quiet line: status first, then what the source is. */
	details: ReactNode;
	at?: number | null;
	/** E.g. hide the time while row tools show in its place. */
	atClassName?: string;
}) {
	return (
		<>
			{mark ? (
				<span className="relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border">
					{mark}
				</span>
			) : null}
			<span className="min-w-0 flex-1">
				<span className="flex min-w-0 items-center gap-1.5">
					{tone ? <StatusDot tone={tone} /> : null}
					<span className="truncate text-sm font-medium">{name}</span>
					{badge}
				</span>
				<span className="block truncate text-xs text-muted-foreground">
					{details}
				</span>
			</span>
			<span
				className={cn("shrink-0 text-xs text-muted-foreground", atClassName)}
			>
				{at != null ? formatShortTimeAgo(at) : null}
			</span>
		</>
	);
}

/** The quiet line's parts, joined with " · "; empty parts drop out. */
export function DetailParts({
	parts,
}: {
	parts: Array<string | null | undefined | false>;
}) {
	// A repeated part says nothing new, and each part keys its own span.
	const shown = [
		...new Set(parts.filter((part): part is string => Boolean(part))),
	];
	return (
		<>
			{shown.map((part, index) => (
				<span key={part}>
					{index > 0 ? " · " : ""}
					<span>{part}</span>
				</span>
			))}
		</>
	);
}
